import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, firestore } from '../services/firebase';
import { User } from '../types';
import { useNotification } from '../context/NotificationContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import React from 'react';

export const useFriendRequests = (inFriendManagementScreen = false) => {
  const [friendRequests, setFriendRequests] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const previousRequestsIds = useRef<string[]>([]);
  const { showToast } = useNotification();

  // Si estamos en la pantalla de gestión de amigos, resetear el contador
  useFocusEffect(
    React.useCallback(() => {
      if (inFriendManagementScreen) {
        resetNotificationCount();
      }
    }, [inFriendManagementScreen])
  );

  // Función para guardar las solicitudes ya notificadas
  const saveNotifiedRequests = async (requestIds: string[]) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      
      await AsyncStorage.setItem(`@friendRequests_${userId}`, JSON.stringify(requestIds));
    } catch (error) {
      console.error("Error al guardar estado de solicitudes:", error);
    }
  };

  // Función para obtener las solicitudes ya notificadas
  const getNotifiedRequests = async (): Promise<string[]> => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return [];
      
      const viewed = await AsyncStorage.getItem(`@friendRequests_${userId}`);
      return viewed ? JSON.parse(viewed) : [];
    } catch (error) {
      console.error("Error al obtener estado de solicitudes:", error);
      return [];
    }
  };

  // Función para resetear el contador de notificaciones
  const resetNotificationCount = async () => {
    try {
      // Guardar todas las solicitudes actuales como ya vistas
      await saveNotifiedRequests([...friendRequests]);
      setNotificationCount(0);
    } catch (error) {
      console.error("Error al resetear contador:", error);
    }
  };

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setLoading(false);
      return;
    }

    const userRef = doc(firestore, 'users', userId);
    
    // Cargar solicitudes ya notificadas al inicio
    getNotifiedRequests().then(notifiedIds => {
      previousRequestsIds.current = notifiedIds;
    });
    
    // Configurar un listener en tiempo real para cambios en las solicitudes de amistad
    const unsubscribe = onSnapshot(userRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data() as User;
        const currentRequests = userData.friendRequests || [];
        
        setFriendRequests(currentRequests);
        
        // Obtenemos las solicitudes ya vistas por el usuario
        const notifiedIds = await getNotifiedRequests();
        
        // Encontrar las nuevas solicitudes (las que están en currentRequests pero no en notifiedIds)
        const newRequests = currentRequests.filter(id => !notifiedIds.includes(id));
        
        // Actualizar el contador de notificaciones
        setNotificationCount(newRequests.length);
        
        // Verificar si se eliminó alguna solicitud y actualizar la lista guardada
        if (notifiedIds.some(id => !currentRequests.includes(id))) {
          const updatedNotifiedIds = notifiedIds.filter(id => currentRequests.includes(id));
          previousRequestsIds.current = updatedNotifiedIds;
          await saveNotifiedRequests(updatedNotifiedIds);
        }
      } else {
        setFriendRequests([]);
        setNotificationCount(0);
        previousRequestsIds.current = [];
        await saveNotifiedRequests([]);
      }
      setLoading(false);
    }, (error) => {
      console.error("Error al obtener solicitudes de amistad:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    friendRequests,
    requestCount: notificationCount,
    loading,
    resetNotificationCount
  };
}; 